import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectGroup, SelectLabel, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Building2, Search, Pencil, MapPin, Loader2, Save,
} from 'lucide-react';
import LogoUpload from './LogoUpload';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const ANCILLARY_OPTIONS = [
  { value: 'fb', label: 'F&B / Restaurant' },
  { value: 'meetings', label: 'Meetings & Events' },
  { value: 'spa', label: 'Spa & Wellness' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'retail', label: 'Retail / Gift Shop' },
  { value: 'parking', label: 'Parking / Valet' },
  { value: 'golf', label: 'Golf' },
  { value: 'waterpark', label: 'Waterpark' },
];

export default function PropertyProfilesManager({ token }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [selectedProp, setSelectedProp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [brands, setBrands] = useState([]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const [propRes, brandRes] = await Promise.all([
        axios.get(`${BACKEND}/api/properties`, { headers }),
        axios.get(`${BACKEND}/api/brands`, { headers }),
      ]);
      setProperties(propRes.data || []);
      setBrands(brandRes.data || []);
    } catch { toast.error('Failed to load properties'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const openEdit = async (prop) => {
    setSelectedProp(prop);
    try {
      const res = await axios.get(`${BACKEND}/api/properties/${prop.id}/attributes`, { headers });
      setEditForm({
        name: prop.name || '', brand_id: prop.brand_id || '',
        address: prop.address || '', address2: prop.address2 || '',
        city: prop.city || '', state: prop.state || '',
        zip_code: prop.zip_code || '', country: prop.country || '',
        phone: prop.phone || '', email: prop.email || '',
        website: prop.website || '', gm_name: prop.gm_name || '',
        gm_email: prop.gm_email || '', year_built: prop.year_built || '',
        year_renovated: prop.year_renovated || '', star_rating: prop.star_rating || '',
        timezone: prop.timezone || '', latitude: prop.latitude || '',
        longitude: prop.longitude || '',
        ...res.data.attributes,
      });
    } catch {
      setEditForm({ name: prop.name || '', property_type: prop.type || 'hotel' });
    }
    setEditOpen(true);
  };

  const saveProfile = async () => {
    if (!selectedProp) return;
    setSaving(true);
    try {
      // Save basic info (name, address, contact, etc.)
      await axios.put(`${BACKEND}/api/admin/properties/${selectedProp.id}/info`, {
        name: editForm.name, address: editForm.address, address2: editForm.address2,
        city: editForm.city, state: editForm.state, zip_code: editForm.zip_code,
        country: editForm.country, phone: editForm.phone, email: editForm.email,
        website: editForm.website, gm_name: editForm.gm_name, gm_email: editForm.gm_email,
        year_built: editForm.year_built, year_renovated: editForm.year_renovated,
        star_rating: editForm.star_rating, timezone: editForm.timezone,
        latitude: editForm.latitude, longitude: editForm.longitude,
      }, { headers });
      // Save operational attributes
      await axios.put(`${BACKEND}/api/admin/properties/${selectedProp.id}/attributes`, editForm, { headers });
      // Save brand
      if (editForm.brand_id !== undefined) {
        await axios.put(`${BACKEND}/api/admin/properties/${selectedProp.id}/brand?brand_id=${editForm.brand_id || ''}`, {}, { headers });
      }
      toast.success('Property profile updated');
      setEditOpen(false);
      fetchProperties();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const updateField = (field, value) => setEditForm(p => ({ ...p, [field]: value }));
  const toggleAncillary = (op) => setEditForm(p => {
    const current = p.ancillary_operations || [];
    return { ...p, ancillary_operations: current.includes(op) ? current.filter(a => a !== op) : [...current, op] };
  });

  const filtered = useMemo(() => {
    if (!search) return properties;
    const q = search.toLowerCase();
    return properties.filter(p => p.name?.toLowerCase().includes(q) || p.id?.toLowerCase().includes(q));
  }, [properties, search]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="property-profiles-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Property Profiles</h2>
          <p className="text-sm text-muted-foreground">Manage property details and attributes used for pricing and platform recommendations</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search properties..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50">
            <th className="p-2.5 text-left text-xs font-semibold">Property</th>
            <th className="p-2.5 text-left text-xs font-semibold">Type</th>
            <th className="p-2.5 text-center text-xs font-semibold">Rooms</th>
            <th className="p-2.5 text-left text-xs font-semibold">Location</th>
            <th className="p-2.5 text-left text-xs font-semibold">Brand</th>
            <th className="p-2.5 text-center text-xs font-semibold">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map(p => {
              const attrs = p.attributes || {};
              return (
              <tr key={p.id} className="border-t hover:bg-muted/20">
                <td className="p-2.5">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <div>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-[10px] text-muted-foreground">{p.id}</div>
                    </div>
                  </div>
                </td>
                <td className="p-2.5">
                  <Badge variant="outline" className="text-[9px] capitalize">{attrs.property_type || p.type || 'hotel'}</Badge>
                  {attrs.service_level && <Badge variant="secondary" className="text-[9px] capitalize ml-1">{attrs.service_level}</Badge>}
                </td>
                <td className="p-2.5 text-center text-xs font-mono">{attrs.total_guestrooms || p.rooms || '—'}</td>
                <td className="p-2.5 text-xs text-muted-foreground">{[p.city, p.state].filter(Boolean).join(', ') || '—'}</td>
                <td className="p-2.5">
                  {p.brand_id ? (
                    <Badge variant="outline" className="text-[9px]" style={{ borderColor: brands.find(b => b.id === p.brand_id)?.color || '#666', color: brands.find(b => b.id === p.brand_id)?.color || '#666' }}>
                      {brands.find(b => b.id === p.brand_id)?.name || p.brand_id}
                    </Badge>
                  ) : <span className="text-[10px] text-muted-foreground">—</span>}
                </td>
                <td className="p-2.5 text-center">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} title="Edit Profile" data-testid={`edit-property-${p.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">No properties found</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4" /> Edit Property - {selectedProp?.name}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            {/* Property Name & Contact */}
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Property Identity</Label>
              <div className="mt-2 mb-3">
                <LogoUpload entityType="property" entityId={selectedProp?.id} logoPath={selectedProp?.logo_path} token={token} onUpdate={fetchProperties} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-[10px]">Property Name</Label>
                  <Input value={editForm.name || ''} onChange={e => updateField('name', e.target.value)} className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Phone</Label>
                  <Input value={editForm.phone || ''} onChange={e => updateField('phone', e.target.value)} placeholder="+1 (555) 123-4567" className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Email</Label>
                  <Input type="email" value={editForm.email || ''} onChange={e => updateField('email', e.target.value)} placeholder="info@hotel.com" className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Website</Label>
                  <Input value={editForm.website || ''} onChange={e => updateField('website', e.target.value)} placeholder="https://..." className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Star Rating</Label>
                  <Select value={editForm.star_rating || ''} onValueChange={v => updateField('star_rating', v)}>
                    <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Not Set</SelectItem>
                      <SelectItem value="1">1 Star</SelectItem>
                      <SelectItem value="2">2 Star</SelectItem>
                      <SelectItem value="3">3 Star</SelectItem>
                      <SelectItem value="4">4 Star</SelectItem>
                      <SelectItem value="5">5 Star</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Address</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="col-span-2">
                  <Label className="text-[10px]">Street Address</Label>
                  <Input value={editForm.address || ''} onChange={e => updateField('address', e.target.value)} placeholder="123 Ocean Drive" className="h-8 text-xs mt-1" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px]">Address Line 2</Label>
                  <Input value={editForm.address2 || ''} onChange={e => updateField('address2', e.target.value)} placeholder="Suite, Floor, etc." className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">City</Label>
                  <Input value={editForm.city || ''} onChange={e => updateField('city', e.target.value)} className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">State / Province</Label>
                  <Input value={editForm.state || ''} onChange={e => updateField('state', e.target.value)} className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">ZIP / Postal Code</Label>
                  <Input value={editForm.zip_code || ''} onChange={e => updateField('zip_code', e.target.value)} className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Country</Label>
                  <Input value={editForm.country || ''} onChange={e => updateField('country', e.target.value)} placeholder="US" className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Latitude</Label>
                  <Input value={editForm.latitude || ''} onChange={e => updateField('latitude', e.target.value)} placeholder="30.1766" className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Longitude</Label>
                  <Input value={editForm.longitude || ''} onChange={e => updateField('longitude', e.target.value)} placeholder="-85.8055" className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Timezone</Label>
                  <Input value={editForm.timezone || ''} onChange={e => updateField('timezone', e.target.value)} placeholder="America/New_York" className="h-8 text-xs mt-1" />
                </div>
              </div>
            </div>

            {/* General Manager */}
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">General Manager</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-[10px]">GM Name</Label>
                  <Input value={editForm.gm_name || ''} onChange={e => updateField('gm_name', e.target.value)} className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">GM Email</Label>
                  <Input type="email" value={editForm.gm_email || ''} onChange={e => updateField('gm_email', e.target.value)} className="h-8 text-xs mt-1" />
                </div>
              </div>
            </div>

            {/* Property History */}
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Property History</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-[10px]">Year Built</Label>
                  <Input value={editForm.year_built || ''} onChange={e => updateField('year_built', e.target.value)} placeholder="1998" className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Year Last Renovated</Label>
                  <Input value={editForm.year_renovated || ''} onChange={e => updateField('year_renovated', e.target.value)} placeholder="2022" className="h-8 text-xs mt-1" />
                </div>
              </div>
            </div>

            {/* Basic Info (Type, Service Level, Rooms, Brand) */}
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Classification</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <Label className="text-[10px]">Property Type</Label>
                  <Select value={editForm.property_type || 'hotel'} onValueChange={v => updateField('property_type', v)}>
                    <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="resort">Resort</SelectItem>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="entertainment">Entertainment</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Service Level</Label>
                  <Select value={editForm.service_level || ''} onValueChange={v => updateField('service_level', v)}>
                    <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Not Set</SelectItem>
                      <SelectItem value="luxury">Luxury</SelectItem>
                      <SelectItem value="full">Full Service</SelectItem>
                      <SelectItem value="select">Select Service</SelectItem>
                      <SelectItem value="economy">Economy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Total Guest Rooms</Label>
                  <Input type="number" value={editForm.total_guestrooms || ''} onChange={e => updateField('total_guestrooms', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <Label className="text-[10px]">Number of Buildings</Label>
                  <Input type="number" value={editForm.number_of_buildings || ''} onChange={e => updateField('number_of_buildings', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Total Property Sq Ft</Label>
                  <Input type="number" value={editForm.total_property_sqft || ''} onChange={e => updateField('total_property_sqft', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Brand</Label>
                  <Select value={editForm.brand_id || ''} onValueChange={v => updateField('brand_id', v)}>
                    <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="No brand" /></SelectTrigger>
                    <SelectContent className="max-h-[350px]">
                      <SelectItem value="">No Brand</SelectItem>
                      {(() => {
                        const groups = {};
                        brands.forEach(b => {
                          const parent = b.parent_company || 'Other';
                          if (!groups[parent]) groups[parent] = [];
                          groups[parent].push(b);
                        });
                        return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0])).map(([parent, bList]) => (
                          <SelectGroup key={parent}>
                            <SelectLabel className="text-[10px] uppercase tracking-wider font-bold">{parent}</SelectLabel>
                            {bList.sort((a, b) => a.name.localeCompare(b.name)).map(b => (
                              <SelectItem key={b.id} value={b.id} className="pl-6">
                                {b.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <Label className="text-[10px]">Annual Reservations</Label>
                  <Input type="number" value={editForm.annual_reservations || ''} onChange={e => updateField('annual_reservations', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" />
                </div>
              </div>
            </div>

            {/* Front Desk / Operations */}
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Front Desk & Operations</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <Label className="text-[10px]">Front Desk Workstations</Label>
                  <Input type="number" value={editForm.front_desk_workstations || ''} onChange={e => updateField('front_desk_workstations', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" />
                </div>
                <div>
                  <Label className="text-[10px]">Housekeeping Staff</Label>
                  <Input type="number" value={editForm.housekeeping_staff || ''} onChange={e => updateField('housekeeping_staff', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" />
                </div>
                <div className="flex items-end gap-3 pb-1">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={editForm.offers_bell_desk || false} onCheckedChange={v => updateField('offers_bell_desk', !!v)} /> Bell Desk
                  </label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={editForm.offers_digital_key || false} onCheckedChange={v => updateField('offers_digital_key', !!v)} /> Digital Key
                  </label>
                </div>
              </div>
            </div>

            {/* Ancillary Operations */}
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ancillary Operations</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ANCILLARY_OPTIONS.map(op => (
                  <button key={op.value} type="button" onClick={() => toggleAncillary(op.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      (editForm.ancillary_operations || []).includes(op.value)
                        ? 'bg-primary/10 border-primary/40 text-primary'
                        : 'bg-muted/40 border-transparent text-muted-foreground hover:border-muted-foreground/20'
                    }`}>
                    {op.label}
                  </button>
                ))}
              </div>
            </div>

            {/* F&B Details */}
            {(editForm.ancillary_operations || []).includes('fb') && (
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Food & Beverage</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div><Label className="text-[10px]">F&B Outlet Count</Label><Input type="number" value={editForm.fb_outlet_count || ''} onChange={e => updateField('fb_outlet_count', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Total F&B Seats</Label><Input type="number" value={editForm.fb_total_seats || ''} onChange={e => updateField('fb_total_seats', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">POS Terminals</Label><Input type="number" value={editForm.fb_pos_terminals || ''} onChange={e => updateField('fb_pos_terminals', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Handheld Devices</Label><Input type="number" value={editForm.fb_handheld_devices || ''} onChange={e => updateField('fb_handheld_devices', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Kitchen Stations</Label><Input type="number" value={editForm.fb_kitchen_stations || ''} onChange={e => updateField('fb_kitchen_stations', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
              </div>
            </div>
            )}

            {/* Meetings & Events */}
            {(editForm.ancillary_operations || []).includes('meetings') && (
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Meetings & Events</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div><Label className="text-[10px]">Event Space (sq ft)</Label><Input type="number" value={editForm.event_space_sqft || ''} onChange={e => updateField('event_space_sqft', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Meeting Rooms</Label><Input type="number" value={editForm.meeting_room_count || ''} onChange={e => updateField('meeting_room_count', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Sales & Catering Staff</Label><Input type="number" value={editForm.sales_catering_staff || ''} onChange={e => updateField('sales_catering_staff', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
              </div>
            </div>
            )}

            {/* Spa */}
            {(editForm.ancillary_operations || []).includes('spa') && (
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Spa & Wellness</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div><Label className="text-[10px]">Treatment Rooms</Label><Input type="number" value={editForm.spa_treatment_rooms || ''} onChange={e => updateField('spa_treatment_rooms', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Providers / Therapists</Label><Input type="number" value={editForm.spa_providers || ''} onChange={e => updateField('spa_providers', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
              </div>
            </div>
            )}

            {/* Parking */}
            {(editForm.ancillary_operations || []).includes('parking') && (
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Parking</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div><Label className="text-[10px]">Parking Spaces</Label><Input type="number" value={editForm.parking_spaces || ''} onChange={e => updateField('parking_spaces', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Valet Zones</Label><Input type="number" value={editForm.valet_zones || ''} onChange={e => updateField('valet_zones', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Daily Parked Vehicles</Label><Input type="number" value={editForm.daily_parked_vehicles || ''} onChange={e => updateField('daily_parked_vehicles', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
              </div>
            </div>
            )}

            {/* Entertainment */}
            {(editForm.ancillary_operations || []).includes('entertainment') && (
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Entertainment</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div><Label className="text-[10px]">Type</Label><Input value={editForm.entertainment_type || ''} onChange={e => updateField('entertainment_type', e.target.value)} placeholder="e.g. Bowling, Arcade" className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Capacity</Label><Input type="number" value={editForm.entertainment_capacity || ''} onChange={e => updateField('entertainment_capacity', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">Lanes / Bays</Label><Input type="number" value={editForm.entertainment_lanes_bays || ''} onChange={e => updateField('entertainment_lanes_bays', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
              </div>
            </div>
            )}

            {/* Retail */}
            {(editForm.ancillary_operations || []).includes('retail') && (
            <div>
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Retail</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div><Label className="text-[10px]">Outlet Count</Label><Input type="number" value={editForm.retail_outlet_count || ''} onChange={e => updateField('retail_outlet_count', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">POS Terminals</Label><Input type="number" value={editForm.retail_pos_terminals || ''} onChange={e => updateField('retail_pos_terminals', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
                <div><Label className="text-[10px]">SKU Count</Label><Input type="number" value={editForm.retail_sku_count || ''} onChange={e => updateField('retail_sku_count', parseInt(e.target.value) || 0)} className="h-8 text-xs mt-1" /></div>
              </div>
            </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveProfile} disabled={saving} className="gap-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
